import { _ } from "../../../../core/polyglot/polyglot.js";
import { event } from "@foretagsplatsen/yaem";
import AsyncStateWidget from "../../../../core/chrome/AsyncStateWidget.js";
import session from "../../../../core/session.js";

/**
 * Concrete document for all pages in Accounting area.
 * Don't subclass this class, create instances instead.
 *
 * @param {AccountingCoordinator} coordinator - Single point of truth holding all the required objects, and ensuring communication
 * @param {boolean} [actionsMenuMobileOnly] - True if the actions menu
 *   should be visible only for mobile devices
 */
export default class AccountingDocument extends AsyncStateWidget {
	constructor({ coordinator, cssClass, actionsMenuMobileOnly = false }) {
		super(...arguments);

		this._initializeEvents();
		this._coordinator = coordinator;
		this._cssClass = cssClass;
		this._actionsMenuMobileOnly = actionsMenuMobileOnly;
		this._resetDocument();

		if (this._coordinator.hasDrawer()) {
			this._coordinator
				.getDrawer()
				.onToggle.register(({ visible }) =>
					this._drawerToggled({ visible }),
				);
		}

		// This is a public field for backward compatible reasons. Please use `getTitle()` instead from now on.
		this.title = this._coordinator.getTitle();
	}

	getTitle() {
		return this._coordinator.getTitle();
	}

	hasNavigation() {
		return true;
	}

	/**
	 * Returns true if the actions menu should be visible only for
	 * mobile devices.
	 */
	isActionsMenuMobileOnly() {
		return this._actionsMenuMobileOnly;
	}

	hasWideActionsMenu() {
		return false;
	}

	hasActionsMenu() {
		return this._coordinator.hasActions();
	}

	shouldNotifyAlerts() {
		return this._coordinator.shouldNotifyAlerts();
	}

	shouldFitViewport() {
		return this._coordinator.shouldDocumentFitViewport();
	}

	navigationIconModeToggled(isDrawerInIconMode) {
		// The parent's width is not yet updated, wait until the next tick.
		setTimeout(
			() =>
				this._coordinator.handleOnDrawerWidthChange({
					isDrawerInIconMode,
				}),
			0,
		);
	}

	/**
	 * This method should be called when the action bar becomes
	 * visible or invisible.
	 */
	actionsVisibilityChanged(_isVisible) {
		// do nothing for now
	}

	async show(...rest) {
		this._resetDocument();

		await this._coordinator.setup(...rest);

		if (this._coordinator.wantsActions()) {
			this.onShowActions.trigger();
		} else {
			this.onHideActions.trigger();
		}

		this._showContent();

		this._coordinator.triggerPostShowWork();
	}

	updatePeriodPicker() {
		// do nothing for now
	}

	/**
	 * Method called each time the global period in the URL is
	 * updated.
	 */
	periodChanged(fallback) {
		this._coordinator.onCurrentPeriodChanged({
			fallback,
			period: session.getPeriod(),
		});
	}

	renderTopActionsOn(html) {
		if (!this._coordinator.hasPeriodPicker()) {
			return;
		}

		html.render(this._coordinator.getPeriodPicker());
	}

	renderMobileOnlyActionsOn(html) {
		if (!this._coordinator.hasPeriodPicker()) {
			return;
		}

		html.label(_("Select period"));
		html.render(this._coordinator.getPeriodPicker());
	}

	renderActionsOn(html) {
		if (!this.hasActionsMenu()) {
			return;
		}

		html.render(this._coordinator.getActions());
	}

	/**
	 * This is legacy event handling. We have to stick to it for now
	 * as this has to be backward compatible.
	 */
	_initializeEvents() {
		this.onUpdateActions = event();
		this.onShowActions = event();
		this.onHideActions = event();
	}

	_renderLoadedContentOn(html) {
		html.div(
			{
				id: "document",
				class: [
					this._cssClass,
					{
						"drawer-menu":
							this._coordinator.hasDrawer() &&
							this._coordinator.getDrawer().isVisible(),
					},
				],
			},
			this._coordinator.getTitle(),
			this._coordinator.getContent(),
			this._renderDrawerMenuOn.bind(this),
		);
	}

	_renderDrawerMenuOn(html) {
		if (!this._coordinator.hasDrawer()) {
			return;
		}

		html.render(this._coordinator.getDrawer());
	}

	_resetDocument() {
		this._showUpdating();

		this.onHideActions.trigger();
	}

	_drawerToggled({ visible }) {
		const document = this.asJQuery().find("#document");

		if (visible) {
			document.addClass("drawer-open");
		} else {
			document.removeClass("drawer-open");
		}
	}
}
